/* KEYFRAMES — the animation model, and nothing else.

   Pure, in the same sense core/doc.js is: no DOM, no three, no S. It takes a
   track and a time and returns a value. That keeps the one part of this
   feature with real arithmetic in it testable on its own, and it keeps the
   module a leaf in the import graph (see the note at the top of
   state/store.js for why that matters here).

   A TRACK is an array of keyframes sorted by time:

       [{ t: 0, v: {x:0,y:0,z:0}, ease: 'inout' }, { t: 2, v: {...} }]

   `t` is seconds from the start of the timeline. `v` is the whole property
   value — a vector for pos/rot/scale, a number for opacity, a hex string for
   colour, a boolean for visible — not one track per axis. That is how After
   Effects treats Position by default, it halves the number of rows the
   timeline has to draw, and splitting a vector track into three scalar ones
   later is a change to the UI rather than to this file.

   `ease` describes the segment LEAVING that keyframe, which is the convention
   every timeline tool uses: you set how a value departs, and the next
   keyframe's own ease has nothing to do with the approach. */

import { clamp } from './util.js';

/* A bezier speed graph is still a later feature; when it arrives it becomes a
   kind whose ease is an array of handles rather than a name, which is why this
   is a string and not a boolean pair.

   The last three OVERSHOOT: their reshaped time leaves [0,1], and mixValue
   extrapolates from it, so the value really does travel past the keyframe and
   come back. That is the whole mechanism — no second code path, no springs at
   playback time, and the result is still an ordinary pair of keyframes that
   can be dragged and re-eased like any other.

   They are refused for colours and booleans, which cannot take an out-of-range
   blend; see evalTrack. */
export const EASES = ['linear', 'in', 'out', 'inout', 'auto', 'hold',
                      'back', 'bounce', 'elastic'];
const OVERSHOOT = new Set(['back', 'bounce', 'elastic']);

/* Cubic, matching CSS's ease-in/ease-out family closely enough that a move
   authored here and a move authored in CSS look like siblings. Not the
   quadratic version: at this duration the difference is visible, and cubic is
   what a designer coming from After Effects expects "ease" to feel like. */
/* ── auto bezier ────────────────────────────────────────────────────────────
   The other five eases reshape TIME between two keyframes and then blend the
   two values linearly. Auto is a different kind of thing and cannot be
   expressed that way: it shapes the VALUE curve so that it passes THROUGH a
   keyframe without stopping there, at a speed that matches the keys either
   side. That takes the neighbours into account, which a function of u alone
   never can.

   The difference from 'inout' is the whole point. Ease in-and-out brings the
   value to a halt at every keyframe, so a run of them reads as a series of
   little arrivals. Auto keeps it moving through, which is what you want for
   a keyframe that is a waypoint rather than a destination — a bounce, an
   arc, anything passing by on its way somewhere else.

   Tangents are in value-per-second, so unevenly spaced keys behave, and they
   are held MONOTONE so the curve never travels outside the values actually
   keyframed — see autoSlope for why that matters and what it costs. At
   either end of a track there is no neighbour on one side, so the segment's
   own secant is used, which leaves the ends straight. */

/**
 * Slope at key `i`, in value units per second. `pick` reads one component.
 *
 * MONOTONE, deliberately. The plain Catmull-Rom slope — the secant across
 * both neighbours — is what most auto-bezier implementations use, and it
 * overshoots: a value that rises and then holds sails past the value it was
 * told to reach and comes back. Measured on 0 → 10 → 10 it peaked at 10.74,
 * and on the reverse it went to -0.74, which for an opacity is out of range
 * and for a scale is inside out.
 *
 * So the tangent is limited by the Fritsch-Carlson conditions, which are the
 * standard way to keep a cubic Hermite monotone:
 *
 *   - a key that is a local peak or trough gets a FLAT tangent. If the value
 *     arrives rising and leaves falling, any non-zero slope there would carry
 *     the curve past it.
 *   - otherwise the slope is capped at three times the gentler of its two
 *     neighbouring secants, which is the sufficient condition for the segment
 *     never to leave the interval its endpoints define.
 *
 * The result still passes THROUGH the key without stopping, which is the
 * whole point of auto — it simply cannot travel outside the values that were
 * actually keyframed. Smoothing rather than embellishment.
 */
function autoSlope(keys, i, pick) {
  const prev = keys[i - 1], next = keys[i + 1], cur = keys[i];
  const dPrev = prev ? (pick(cur.v) - pick(prev.v)) / Math.max(1e-9, cur.t - prev.t) : null;
  const dNext = next ? (pick(next.v) - pick(cur.v)) / Math.max(1e-9, next.t - cur.t) : null;
  if (dPrev === null && dNext === null) return 0;
  /* One-sided at the ends: the segment's own secant, which leaves the curve
     straight there rather than inventing a direction from nothing. */
  if (dPrev === null) return dNext;
  if (dNext === null) return dPrev;
  /* Opposite signs means a peak or a trough; either being zero means a flat
     run, where any slope at all would bulge away from a value that is not
     changing. Both want the same answer. */
  if (dPrev * dNext <= 0) return 0;
  const m = (pick(next.v) - pick(prev.v)) / Math.max(1e-9, next.t - prev.t);
  const cap = 3 * Math.min(Math.abs(dPrev), Math.abs(dNext));
  return Math.sign(m) * Math.min(Math.abs(m), cap);
}

/** One component of a cubic Hermite across a segment of length `h`. */
function hermite(p0, p1, m0, m1, u, h) {
  const u2 = u * u, u3 = u2 * u;
  return (2*u3 - 3*u2 + 1) * p0
       + (u3 - 2*u2 + u) * h * m0
       + (-2*u3 + 3*u2) * p1
       + (u3 - u2) * h * m1;
}

/* Only numbers and vectors can carry a tangent. A colour could in principle
   be splined channel-wise, but "overshoot" on a colour means a channel out of
   gamut, and a boolean has no midpoint at all — so both fall back to the
   smoothest thing the reshape-time model can offer. */
const canSpline = v => typeof v === 'number' || (v && typeof v === 'object');

function autoSegment(keys, i, u) {
  const a = keys[i], b = keys[i + 1];
  const h = b.t - a.t;
  const seg = (pick) => (pick(b.v) - pick(a.v)) / Math.max(1e-9, h);
  /* A non-auto endpoint contributes its own secant, which is exactly the
     value that makes the Hermite collapse back to a straight line — so this
     generalises the old behaviour rather than replacing it. */
  const m = (idx, pick) => (keys[idx].ease === 'auto' ? autoSlope(keys, idx, pick) : seg(pick));

  if (typeof a.v === 'number') {
    const id = x => x;
    return hermite(a.v, b.v, m(i, id), m(i + 1, id), u, h);
  }
  const out = {};
  for (const k in a.v) {
    const pick = v => (v?.[k] ?? a.v[k]);
    out[k] = hermite(a.v[k], b.v[k] ?? a.v[k], m(i, pick), m(i + 1, pick), u, h);
  }
  return out;
}

/* ── custom curves ─────────────────────────────────────────────────────────
   An ease is either one of the nine NAMES above or four numbers: the two
   control points of a cubic bezier, exactly as CSS spells it. Four numbers on
   ONE keyframe rather than a handle on each of two, because CSS describes a
   whole segment with two control points and the invariant here is already
   "the ease describes the segment LEAVING this key". So a curve changes what
   an ease IS and nothing about what a keyframe is — setKey, moveKey, the
   drag, copy/paste and replace-on-drop all keep working untouched.

   x is clamped to [0,1] and y is not. A control point whose x leaves the unit
   interval makes the curve double back, and a segment that maps one instant
   to two values has no defined value at all; y leaving it is the overshoot,
   and is the point. */
export const isCurve = e => Array.isArray(e) && e.length === 4 && e.every(Number.isFinite);

/** True when a curve carries its value outside the pair it joins — the same
 *  question OVERSHOOT answers for the three named ones. */
export const curveOvershoots = e => isCurve(e) && (e[1] < 0 || e[1] > 1 || e[3] < 0 || e[3] > 1);

/* Bezier basis for one axis, with the endpoints pinned at 0 and 1. */
const bz = (s, p1, p2) => {
  const r = 1 - s;
  return 3 * r * r * s * p1 + 3 * r * s * s * p2 + s * s * s;
};
const bzSlope = (s, p1, p2) => {
  const r = 1 - s;
  return 3 * r * r * p1 + 6 * r * s * (p2 - p1) + 3 * s * s * (1 - p2);
};

/**
 * y at time u along cubic-bezier(x1,y1,x2,y2).
 *
 * The curve is parametric, so x has to be inverted before y can be read:
 * find s where x(s) = u, then return y(s). Newton first because it converges
 * in a handful of steps over almost the whole range, with bisection as the
 * fallback for the flat spots where the slope is too small to divide by —
 * a control point at x=0 or x=1 makes exactly that, and it is a shape people
 * ask for (cubic-bezier(0,0,.2,1) is an ordinary "snap then settle").
 */
function bezierAt(u, [x1, y1, x2, y2]) {
  if (u <= 0) return 0;
  if (u >= 1) return 1;
  let s = u;
  for (let i = 0; i < 8; i++) {
    const d = bzSlope(s, x1, x2);
    if (Math.abs(d) < 1e-6) break;
    const err = bz(s, x1, x2) - u;
    if (Math.abs(err) < 1e-7) return bz(s, y1, y2);
    s -= err / d;
  }
  /* Bisection cannot fail: x is monotone because x1 and x2 are clamped into
     [0,1], so the root is bracketed and halving always closes on it. */
  let lo = 0, hi = 1;
  s = u;
  for (let i = 0; i < 24; i++) {
    const x = bz(s, x1, x2);
    if (Math.abs(x - u) < 1e-7) break;
    if (x < u) lo = s; else hi = s;
    s = (lo + hi) / 2;
  }
  return bz(s, y1, y2);
}

/** The nine names as handles, for drawing them and for starting a curve from
 *  one. `auto` and `hold` are absent on purpose: neither is a time reshape
 *  (see autoSegment, and evalTrack's short-circuit for hold), so neither has
 *  an honest pair of control points. */
export const CURVE_PRESETS = {
  linear: [0, 0, 1, 1],
  in:     [0.32, 0, 0.67, 0],
  out:    [0.33, 1, 0.68, 1],
  inout:  [0.65, 0, 0.35, 1],
  back:   [0.36, 0, 0.66, 1.56],
  bounce: [0.28, 1.4, 0.6, 1],
  elastic:[0.22, 1.7, 0.6, 0.92]
};

function shape(u, ease) {
  if (isCurve(ease)) return bezierAt(u, ease);
  switch (ease) {
    case 'in':    return u * u * u;
    case 'out':   return 1 - Math.pow(1 - u, 3);
    case 'inout': return u < .5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
    /* Reached only for values that cannot carry a tangent — a colour or a
       boolean (see canSpline). Smooth is the nearest honest reading of
       "auto" there; the real thing happens in autoSegment. */
    case 'auto':  return u < .5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
    /* The three below are the standard Penner "out" curves, the shapes every
       motion tool means by these words — a value that arrives at speed and
       settles, rather than one that departs oddly. Only the out direction is
       offered: an entrance that overshoots on the way IN reads as a mistake,
       and anyone who wants that can author the peak as its own keyframe. */

    /* Sails past the target and eases back. c1 sets how far — 1.70158 is the
       traditional value and lands about 10% beyond. */
    case 'back': {
      const c1 = 1.70158, c3 = c1 + 1, x = u - 1;
      return 1 + c3 * x * x * x + c1 * x * x;
    }
    /* Four parabolic hops of decreasing height, each landing ON the target.
       Unlike the other two this never passes it — it approaches from one side
       — so it is the one that is safe on a value with a hard floor. */
    case 'bounce': {
      const n1 = 7.5625, d1 = 2.75;
      if (u < 1 / d1) return n1 * u * u;
      if (u < 2 / d1) { const x = u - 1.5 / d1; return n1 * x * x + 0.75; }
      if (u < 2.5 / d1) { const x = u - 2.25 / d1; return n1 * x * x + 0.9375; }
      const x = u - 2.625 / d1;
      return n1 * x * x + 0.984375;
    }
    /* A decaying oscillation about the target. The endpoints are returned
       exactly rather than computed, because the decay only reaches the target
       in the limit and a keyframe has to actually arrive. */
    case 'elastic': {
      if (u <= 0) return 0;
      if (u >= 1) return 1;
      return Math.pow(2, -10 * u) * Math.sin((u * 10 - 0.75) * (2 * Math.PI / 3)) + 1;
    }
    /* 'hold' never reaches here — evalTrack short-circuits before
       interpolating, because holding is the absence of interpolation rather
       than a curve that happens to be flat. Listed for completeness. */
    default:      return u;
  }
}

/* ── value kinds ───────────────────────────────────────────────────────────
   Four shapes of value travel through a track, and each needs its own copy
   and its own blend. Dispatching on the value itself rather than on a
   declared type keeps the keyframe shape uniform — a track does not have to
   record what kind it is, because its values already say. */

/** A defensive copy, so a keyframe can never alias the live object it was
 *  captured from. Without this, moving an object would silently rewrite the
 *  keyframe that was supposed to remember where it used to be. */
export function copyValue(v) {
  if (v && typeof v === 'object') return { ...v };
  return v;
}

/* Colour blends through linear-light RGB rather than straight down the hex
   digits. sRGB hex values are gamma-encoded, so lerping them directly dips
   through a muddy dark band — the classic red-to-green fade that goes via
   brown. Squaring on the way in and square-rooting on the way out is the
   cheap approximation of that conversion, and at these step sizes it is
   indistinguishable from the exact transfer function while costing two
   multiplies instead of a pow(). */
const hexToRgb = h => {
  const n = parseInt(String(h).replace('#', ''), 16);
  return Number.isFinite(n) ? [(n >> 16) & 255, (n >> 8) & 255, n & 255] : [0, 0, 0];
};
const rgbToHex = c => '#' + c.map(x => clamp(Math.round(x), 0, 255).toString(16).padStart(2, '0')).join('');

function mixColor(a, b, u) {
  const A = hexToRgb(a), B = hexToRgb(b);
  const out = [0, 1, 2].map(i => {
    const la = (A[i] / 255) ** 2, lb = (B[i] / 255) ** 2;
    return Math.sqrt(la + (lb - la) * u) * 255;
  });
  return rgbToHex(out);
}

/** Blend two keyframe values of the same shape. */
export function mixValue(a, b, u) {
  if (typeof a === 'number') return a + (b - a) * u;
  /* A boolean has no midpoint, so it steps at the far end rather than
     flickering at 0.5 — which would make a `visible` track turn on halfway
     between two keys that both said false. */
  if (typeof a === 'boolean') return u >= 1 ? b : a;
  if (typeof a === 'string') return mixColor(a, b, u);
  if (a && typeof a === 'object') {
    const out = {};
    for (const k in a) out[k] = a[k] + ((b?.[k] ?? a[k]) - a[k]) * u;
    return out;
  }
  return u >= 1 ? b : a;
}

/* ── evaluation ──────────────────────────────────────────────────────────── */

/**
 * The value of a track at time `t`.
 *
 * Outside the keyframed range the value CLAMPS to the nearest key rather than
 * extrapolating. Extrapolation would mean an object that keeps accelerating
 * off the screen after its last keyframe, which is never what was meant and
 * is invisible until export.
 *
 * @returns the interpolated value, or `undefined` for an empty track — which
 *          the caller must read as "leave this property alone", not as a
 *          value to assign.
 */
export function evalTrack(keys, t) {
  if (!keys || !keys.length) return undefined;
  if (keys.length === 1 || t <= keys[0].t) return copyValue(keys[0].v);
  const last = keys[keys.length - 1];
  if (t >= last.t) return copyValue(last.v);

  let i = 0;
  while (i < keys.length - 1 && keys[i + 1].t <= t) i++;
  const a = keys[i], b = keys[i + 1];
  /* Holding, and a zero-length segment (two keys at the same instant, which
     is how a hard cut gets authored) both resolve to the earlier value
     without touching the blend. */
  if (a.ease === 'hold' || b.t <= a.t) return copyValue(a.v);
  const u = clamp((t - a.t) / (b.t - a.t), 0, 1);
  /* Either endpoint being 'auto' makes this segment a spline, because a
     tangent at one end shapes the whole span between them. */
  if ((a.ease === 'auto' || b.ease === 'auto') && canSpline(a.v))
    return autoSegment(keys, i, u);
  /* An overshooting ease on a value that cannot overshoot falls back to the
     smoothest honest reading, exactly as 'auto' does above. It is not a
     nicety: mixColor would square-root a negative and hand back a colour of
     NaN, and a boolean steps the instant the reshaped time reaches 1, so an
     elastic curve would flip `visible` in the middle of its own segment. */
  const overshoots = OVERSHOOT.has(a.ease) || curveOvershoots(a.ease);
  const ease = overshoots && !canSpline(a.v) ? 'inout' : a.ease;
  return mixValue(a.v, b.v, shape(u, ease));
}

/* ── editing ───────────────────────────────────────────────────────────────
   Every mutation goes through these so the sorted-by-time invariant holds
   everywhere. evalTrack's scan depends on it and would silently return the
   wrong segment if a caller pushed a key out of order. */

/* Two keyframes closer together than this are the same keyframe. Dragging a
   gizmo writes a key on every pointermove at whatever the playhead reads, and
   without a tolerance a scrub of a thousandth of a frame would leave a second
   key nobody asked for. Half a frame at 120fps — finer than any timeline UI
   can address, coarser than float noise. */
const SAME_TIME = 1 / 240;

/**
 * Set (or replace) the keyframe at `t`. Returns the track, sorted.
 *
 * @param {Array} keys   existing track, or a falsy value to start one
 * @param {number} t     seconds
 * @param {*} v          the whole property value; copied, never aliased
 * @param {string} [ease] defaults to the ease already at this time, else 'inout'
 */
export function setKey(keys, t, v, ease) {
  const track = Array.isArray(keys) ? keys.slice() : [];
  const at = track.findIndex(k => Math.abs(k.t - t) < SAME_TIME);
  /* Replacing keeps the ease that was already there unless a new one was
     asked for: nudging a keyframe's value should not quietly reset a curve
     someone chose. */
  const keep = at >= 0 ? track[at].ease : 'inout';
  const key = { t: at >= 0 ? track[at].t : t, v: copyValue(v), ease: ease || keep };
  if (at >= 0) track[at] = key; else track.push(key);
  track.sort((p, q) => p.t - q.t);
  return track;
}

/** Drop the keyframe at `t`. Returns the track, or null once it is empty —
 *  an empty track is not a track, and leaving `[]` behind would make
 *  `o.anim.pos` truthy with nothing in it. */
export function removeKey(keys, t) {
  const track = (keys || []).filter(k => Math.abs(k.t - t) >= SAME_TIME);
  return track.length ? track : null;
}

/** Move a keyframe in time, keeping the track sorted. Merges onto whatever is
 *  already at the destination, matching what dragging one key onto another
 *  visibly does. */
export function moveKey(keys, from, to) {
  const k = (keys || []).find(x => Math.abs(x.t - from) < SAME_TIME);
  if (!k) return keys;
  return setKey(removeKey(keys, from), Math.max(0, to), k.v, k.ease);
}

/** True if `t` lands on a keyframe — what the timeline's add/remove toggle
 *  and the diamond markers both ask. */
export const hasKeyAt = (keys, t) => !!(keys || []).some(k => Math.abs(k.t - t) < SAME_TIME);

/* ── validation ────────────────────────────────────────────────────────────
   Documents arrive from disk, from the cloud, and from hand-edited .json. The
   same posture core/doc.js's migrate() takes: repair what is merely old,
   discard what is not well-formed, and never let a malformed shape reach the
   evaluator — which runs sixty times a second and has no business
   second-guessing its input. */

/** Every property that can carry a track, and the shape its value must have.
 *  The single source of truth for "is this animatable" — the inspector, the
 *  timeline and the validator all read it, so adding a property later is one
 *  line here rather than four edits that can disagree. */
export const ANIMATABLE = {
  pos:       { kind: 'vec3',   label: 'Position' },
  /* THE THREE AXES OF A VECTOR, as tracks of their own.

     A position, rotation or scale is animated as ONE track holding a whole
     {x,y,z} — half the rows, and how most people first think of it. But a
     bounce is Y alone and a slide is X alone, and one track cannot hold Y still
     while X moves: every key stamps all three. So a vector may instead be
     SEPARATED into three number tracks, named 'pos.x', 'pos.y', 'pos.z' and so
     on, each with its own keys and easing.

     They are ordinary number tracks — the timeline, the curve editor, dragging,
     copy and paste and saving all work on them with no special case, which is
     the point of making them properties rather than a mode. What IS special is
     the pairing: a vector is either combined ('pos') or separated ('pos.x'…),
     never both, and splitVecTrack / mergeAxisTracks below are how it changes
     from one to the other. Kept apart from animatableProps: they are how a
     property is keyframed, not another property an object has. */
  'pos.x':   { kind: 'number', label: 'Position X', of: 'pos',   axis: 'x' },
  'pos.y':   { kind: 'number', label: 'Position Y', of: 'pos',   axis: 'y' },
  'pos.z':   { kind: 'number', label: 'Position Z', of: 'pos',   axis: 'z' },
  rot:       { kind: 'vec3',   label: 'Rotation' },
  'rot.x':   { kind: 'number', label: 'Rotation X', of: 'rot',   axis: 'x' },
  'rot.y':   { kind: 'number', label: 'Rotation Y', of: 'rot',   axis: 'y' },
  'rot.z':   { kind: 'number', label: 'Rotation Z', of: 'rot',   axis: 'z' },
  scale:     { kind: 'vec3',   label: 'Scale' },
  'scale.x': { kind: 'number', label: 'Scale X',    of: 'scale', axis: 'x' },
  'scale.y': { kind: 'number', label: 'Scale Y',    of: 'scale', axis: 'y' },
  'scale.z': { kind: 'number', label: 'Scale Z',    of: 'scale', axis: 'z' },
  opacity:   { kind: 'number', label: 'Opacity' },
  color:     { kind: 'color',  label: 'Colour' },
  intensity: { kind: 'number', label: 'Intensity' },
  visible:   { kind: 'bool',   label: 'Visible' },
  /* Text Area only: how much of the block has arrived, 0 to 1, revealed a
     word at a time. A property rather than a mode of its own, so the reveal
     is ordinary keyframes on the ordinary timeline — scrubbable, re-timable,
     undoable, and exported by the same path as everything else. */
  reveal:    { kind: 'number', label: 'Reveal' },
  /* The other edge of the same wipe: how much has LEFT, counted from the
     same end. One edge can only express one exit — run reveal backwards and
     the last word goes first, because no value of a single number says "the
     first word has gone and the rest are still here". */
  erase:     { kind: 'number', label: 'Erase' }
};

/* The vectors that can be separated, and the three tracks each becomes. */
export const AXES = ['x', 'y', 'z'];
export const AXIS_TRACKS = {
  pos:   ['pos.x', 'pos.y', 'pos.z'],
  rot:   ['rot.x', 'rot.y', 'rot.z'],
  scale: ['scale.x', 'scale.y', 'scale.z']
};

/** Which of a vector's axis tracks an object currently has. */
export const axisTracksPresent = (o, base) => (AXIS_TRACKS[base] || []).filter(p => !!o?.anim?.[p]);

const copyEase = e => (Array.isArray(e) ? e.slice() : e);

/** One vector track → three number tracks, key for key: the same times and the
 *  same easing, so separating loses nothing. */
export function splitVecTrack(keys) {
  const out = { x: [], y: [], z: [] };
  for (const k of keys)
    for (const a of AXES) out[a].push({ t: k.t, v: k.v[a], ease: copyEase(k.ease) });
  return out;
}

/**
 * Up to three number tracks → one vector track.
 *
 * A key lands at every time ANY axis has one, valued by evaluating each axis
 * there — so the motion is unchanged, but an axis that had no key at a time now
 * has one (holding whatever it was doing). An axis with no track at all holds
 * `fallback`. The easing of a key is its own axis's; where several axes keyed
 * the same time with different easing, the first of x, y, z wins — combining is
 * the one direction that can lose something, which is why it is a choice.
 *
 * @param {{x?:object[], y?:object[], z?:object[]}} tracks
 * @param {{x:number, y:number, z:number}} fallback
 */
export function mergeAxisTracks(tracks, fallback) {
  const times = [];
  for (const a of AXES)
    for (const k of tracks[a] || [])
      if (!times.some(t => Math.abs(t - k.t) < 1e-6)) times.push(k.t);
  times.sort((p, q) => p - q);
  return times.map(t => {
    const v = {};
    let ease = null;
    for (const a of AXES) {
      const track = tracks[a];
      v[a] = track ? evalTrack(track, t) : fallback[a];
      const own = track && track.find(k => Math.abs(k.t - t) < 1e-6);
      if (own && ease === null) ease = copyEase(own.ease);
    }
    return { t, v, ease: ease ?? 'inout' };
  });
}

/**
 * Combine a separated vector back into one track, in place: the object's own
 * `anim` block changes and nothing else does — no re-evaluation, no event — so
 * a caller that is about to re-evaluate anyway (linking, unlinking) can use it
 * directly. state/timeline.js's combineProp is this plus the re-evaluation.
 * @returns {boolean} whether there was anything to combine
 */
export function combineAxes(o, base) {
  const present = axisTracksPresent(o, base);
  if (!present.length) return false;
  const tracks = {};
  for (const a of AXES) if (o.anim[base + '.' + a]) tracks[a] = o.anim[base + '.' + a];
  o.anim[base] = mergeAxisTracks(tracks, { x: o[base].x, y: o[base].y, z: o[base].z });
  for (const p of AXIS_TRACKS[base]) delete o.anim[p];
  return true;
}

/**
 * Which of those an object may actually animate.
 *
 * ANIMATABLE says what a track CAN hold; this says what makes sense on one
 * particular object, and it is the authority — every feature that offers to
 * animate something asks here. It lived in the Motion inspector until the
 * move library needed the same answer; a second copy of these rules is how
 * they quietly stop agreeing.
 *
 * Reads nothing but the object's own type and flags, so this file stays a
 * leaf with no imports beyond util.
 */
export function animatableProps(o){
  const out = [];
  /* A frame and a recess are fixed fixtures — they always sit centred on the
     screen, which is why Design hides their Transform entirely. Animating a
     transform that the app itself keeps overwriting would be a trap. */
  const fixed = o.type === 'frame' || o.type === 'recess';
  const isLamp = o.type === 'light', isCam = o.type === 'camera';
  /* A null is a transform and nothing else: nothing to fade, colour or hide in a
     render, since it never appears in one. */
  if(o.type === 'null') return ['pos', 'rot', 'scale'];
  /* Order is the order the rows appear in. Visible leads because it is the
     one property every object has and the cheapest thing to animate — a
     thing that appears and disappears needs no other track. Then the three
     transforms as a block, since they are read and edited together. Then
     the two that describe how it looks, weakest last. */
  if(!isCam) out.push('visible');
  if(!fixed && !isCam) out.push('pos');
  /* Rotation is skipped for a billboarded object for the same reason the
     gizmo hides its rotate handles: applyBillboards overwrites node rotation
     every frame, so a rotation track would silently do nothing. */
  if(!fixed && !isCam && !o.faceCamera && (!isLamp || o.params.kind === 'spot' || o.params.kind === 'directional'))
    out.push('rot');
  if(!fixed && !isLamp && !isCam) out.push('scale');
  if(!isLamp && !isCam) out.push('opacity');
  /* A lamp's own version of "how strong" — it has no opacity, so this sits
     in the same slot rather than trailing after the colour. */
  if(isLamp) out.push('intensity');
  if(!isCam) out.push('color');
  /* Last, and only here: a Text Area is the one object made of words, so it
     is the only one with anything to reveal. 3D Text cannot — it is a single
     extruded mesh, with no separate words to hold back. */
  if(o.type === 'textarea') out.push('reveal', 'erase');
  return out;
}

const wellFormed = (kind, v) => {
  if (kind === 'number') return Number.isFinite(v);
  if (kind === 'bool')   return typeof v === 'boolean';
  if (kind === 'color')  return typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v);
  return !!v && typeof v === 'object' && ['x', 'y', 'z'].every(k => Number.isFinite(v[k]));
};

/**
 * Normalise an object's `anim` block. Returns a clean block, or undefined if
 * there is nothing usable in it — so the caller can `delete o.anim` rather
 * than carry an empty object into every save.
 */
export function normalizeAnim(anim) {
  if (!anim || typeof anim !== 'object') return undefined;
  const out = {};
  for (const [prop, spec] of Object.entries(ANIMATABLE)) {
    const raw = anim[prop];
    if (!Array.isArray(raw)) continue;
    const keys = raw
      .filter(k => k && Number.isFinite(k.t) && k.t >= 0 && wellFormed(spec.kind, k.v))
      .map(k => ({
        t: k.t,
        v: copyValue(k.v),
        /* A boolean track is a hold track by definition — there is nothing
           between true and false to interpolate through, so an 'inout' saved
           onto one (by hand, or by a future bug) is corrected rather than
           honoured. */
        /* A curve survives as four numbers with x clamped back into [0,1] —
           see isCurve for why x is the one that cannot be trusted. Anything
           that is neither a known name nor a well-formed curve becomes
           'inout', which is what an older build meeting a newer file does
           too: the timing is kept and only the shaping is lost. */
        ease: spec.kind === 'bool' ? 'hold'
            : isCurve(k.ease) ? [clamp(k.ease[0], 0, 1), k.ease[1], clamp(k.ease[2], 0, 1), k.ease[3]]
            : (EASES.includes(k.ease) ? k.ease : 'inout')
      }))
      .sort((a, b) => a.t - b.t);
    if (keys.length) out[prop] = keys;
  }
  /* Combined or separated, never both — a hand-edited file could carry the two,
     and the axis tracks would silently override the vector one every frame. The
     vector track is kept: it is the older form, and the one an older build
     understands. */
  for (const [base, axes] of Object.entries(AXIS_TRACKS))
    if (out[base]) for (const p of axes) delete out[p];
  return Object.keys(out).length ? out : undefined;
}

/** Does this object have any animation at all? */
export const isAnimated = o => !!o?.anim && Object.keys(o.anim).length > 0;

/** The last keyframe time across a whole document — what "your animation is
 *  longer than the timeline" needs in order to say so. */
export function contentEnd(objects) {
  let end = 0;
  for (const o of objects || [])
    for (const keys of Object.values(o.anim || {}))
      if (keys.length) end = Math.max(end, keys[keys.length - 1].t);
  return end;
}
